import type { PropsWithChildren } from "react";
import { createContext, useContext, useMemo } from "react";
import { useLocation } from "react-router-dom";
import { useWorkspace } from "../context/WorkspaceContext";
import useDrivers from "../hooks/useDrivers";
import useCustomers from "../hooks/useCustomers";
import useProducts from "../hooks/useProducts";
import useLocations from "../hooks/useLocations";
import { useVehicles } from "../hooks/useVehicles";
import useCompanies from "../hooks/useCompanies";
import useWorkspaceSettings from "../hooks/useWorkspaceSettings";
import { Driver, Customer, Product, Location, Vehicle, Company, WorkspaceSettings } from "../models";

/**
 * Central store for the workspace's master / reference data.
 *
 * Problem this solves: pages and components used to each re-declare the same
 * fetch boilerplate — `useDrivers(activeWorkspace?.workspaceId ?? "")` followed
 * by `useMemo(() => q.data ?? [], [q.data])` — for the same handful of
 * workspace-wide collections. The queries deduplicated at the React Query
 * level, but the pattern was repeated everywhere and obscured the fact that the
 * data is shared.
 *
 * `WorkspaceDataProvider` loads those collections exactly once, when the app
 * has an active workspace, and exposes them as ready-to-use arrays through
 * `useWorkspaceData()`. It is still backed by the same cached, workspace-aware
 * hooks, so:
 *   - request deduplication and the `cacheKey` invalidation mechanism keep
 *     working (a write that calls `updateCacheKey()` / `useDataCache` refreshes
 *     the data here automatically);
 *   - other consumers of the underlying hooks share the exact same cache entry.
 *
 * The provider is gated on an active workspace, so logged-out / public routes
 * never trigger these reads.
 */
export interface WorkspaceDataValue {
  drivers: Driver[];
  customers: Customer[];
  products: Product[];
  locations: Location[];
  vehicles: Vehicle[];
  companies: Company[];
  /** Workspace-level settings document, or `null` when none exists yet. */
  workspaceSettings: WorkspaceSettings | null;
  /** Per-dataset loading state (initial load or background refetch). */
  loading: {
    drivers: boolean;
    customers: boolean;
    products: boolean;
    locations: boolean;
    vehicles: boolean;
    companies: boolean;
    workspaceSettings: boolean;
  };
  /**
   * Per-dataset error state. Consumers must distinguish "the fetch failed" from
   * "there is genuinely no data": an empty array is returned in both cases, so
   * anything that acts on emptiness (e.g. the onboarding gate) needs this flag
   * to avoid treating a transient failure as an unconfigured workspace.
   */
  error: {
    drivers: boolean;
    customers: boolean;
    products: boolean;
    locations: boolean;
    vehicles: boolean;
    companies: boolean;
    workspaceSettings: boolean;
  };
}

const EMPTY_VALUE: WorkspaceDataValue = {
  drivers: [],
  customers: [],
  products: [],
  locations: [],
  vehicles: [],
  companies: [],
  workspaceSettings: null,
  loading: {
    drivers: false,
    customers: false,
    products: false,
    locations: false,
    vehicles: false,
    companies: false,
    workspaceSettings: false,
  },
  error: {
    drivers: false,
    customers: false,
    products: false,
    locations: false,
    vehicles: false,
    companies: false,
    workspaceSettings: false,
  },
};

const WorkspaceDataContext = createContext<WorkspaceDataValue>(EMPTY_VALUE);

const ActiveWorkspaceDataProvider = ({ children }: PropsWithChildren) => {
  // Each hook resolves the active workspace from context and shares its cache
  // entry with every other caller of the same hook.
  const driversQuery = useDrivers();
  const customersQuery = useCustomers();
  const productsQuery = useProducts();
  const locationsQuery = useLocations();
  const vehiclesQuery = useVehicles();
  const companiesQuery = useCompanies();
  const workspaceSettingsQuery = useWorkspaceSettings();

  const value = useMemo<WorkspaceDataValue>(
    () => ({
      drivers: driversQuery.data ?? [],
      customers: customersQuery.data ?? [],
      products: productsQuery.data ?? [],
      locations: locationsQuery.data ?? [],
      vehicles: vehiclesQuery.data ?? [],
      companies: companiesQuery.data ?? [],
      workspaceSettings: workspaceSettingsQuery.data ?? null,
      loading: {
        drivers: driversQuery.isLoading || driversQuery.isRefetching,
        customers: customersQuery.isLoading || customersQuery.isRefetching,
        products: productsQuery.isLoading || productsQuery.isRefetching,
        locations: locationsQuery.isLoading || locationsQuery.isRefetching,
        vehicles: vehiclesQuery.isLoading || vehiclesQuery.isRefetching,
        companies: companiesQuery.isLoading || companiesQuery.isRefetching,
        workspaceSettings:
          workspaceSettingsQuery.isLoading ||
          workspaceSettingsQuery.isRefetching,
      },
      error: {
        drivers: driversQuery.isError,
        customers: customersQuery.isError,
        products: productsQuery.isError,
        locations: locationsQuery.isError,
        vehicles: vehiclesQuery.isError,
        companies: companiesQuery.isError,
        workspaceSettings: workspaceSettingsQuery.isError,
      },
    }),
    [
      driversQuery.data,
      driversQuery.isLoading,
      driversQuery.isRefetching,
      driversQuery.isError,
      customersQuery.data,
      customersQuery.isLoading,
      customersQuery.isRefetching,
      customersQuery.isError,
      productsQuery.data,
      productsQuery.isLoading,
      productsQuery.isRefetching,
      productsQuery.isError,
      locationsQuery.data,
      locationsQuery.isLoading,
      locationsQuery.isRefetching,
      locationsQuery.isError,
      vehiclesQuery.data,
      vehiclesQuery.isLoading,
      vehiclesQuery.isRefetching,
      vehiclesQuery.isError,
      companiesQuery.data,
      companiesQuery.isLoading,
      companiesQuery.isRefetching,
      companiesQuery.isError,
      workspaceSettingsQuery.data,
      workspaceSettingsQuery.isLoading,
      workspaceSettingsQuery.isRefetching,
      workspaceSettingsQuery.isError,
    ],
  );

  return (
    <WorkspaceDataContext.Provider value={value}>
      {children}
    </WorkspaceDataContext.Provider>
  );
};

export const WorkspaceDataProvider = ({ children }: PropsWithChildren) => {
  const { activeWorkspace } = useWorkspace();
  const { pathname } = useLocation();

  // Standalone public and authentication surfaces never need workspace master
  // data, even when a signed-in workspace remains cached in the browser.
  const isStandaloneRoute =
    pathname.startsWith("/order/") ||
    pathname === "/order" ||
    pathname.startsWith("/shopify/");
  const isAuthRoute = [
    "/login",
    "/logout",
    "/signup",
    "/oauth/authorize",
    "/admin-mcp/oauth/authorize",
    "/load_workspace",
    "/reset_password",
  ].some((prefix) => pathname.startsWith(prefix));

  // Don't touch Firestore until a workspace is active (e.g. login / public form).
  if (isStandaloneRoute || isAuthRoute || !activeWorkspace?.workspaceId) {
    return (
      <WorkspaceDataContext.Provider value={EMPTY_VALUE}>
        {children}
      </WorkspaceDataContext.Provider>
    );
  }

  // `key` remounts the store (and clears stale data) when the workspace changes.
  return (
    <ActiveWorkspaceDataProvider key={activeWorkspace.workspaceId}>
      {children}
    </ActiveWorkspaceDataProvider>
  );
};

/**
 * Access the workspace's shared master data. Returns ready-to-use arrays plus
 * per-dataset loading flags — no `workspaceId` plumbing, no `data ?? []`.
 */
export const useWorkspaceData = (): WorkspaceDataValue =>
  useContext(WorkspaceDataContext);
